import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, X, ScrollText, Building2, User, Clock } from "lucide-react";

/** /platform/audit — platform-wide auditlog met filterbar. */
export default function PlatformAuditPage() {
  const [orgs, setOrgs] = useState([]);
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ actions: [], entities: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ organization_id: "", action: "", entity: "", user_email: "", q: "" });

  const loadOrgs = async () => {
    try {
      const r = await api.get("/platform/overview");
      setOrgs(r.data.items || []);
    } catch { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== "__all__") params.append(k, v); });
      params.append("limit", "200");
      const r = await api.get(`/platform/audit?${params.toString()}`);
      setItems(r.data.items || []);
      setFacets(r.data.facets || { actions: [], entities: [] });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadOrgs(); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* debounce */
    // eslint-disable-next-line
  }, [filters]);

  const set = (k) => (v) => setFilters(f => ({ ...f, [k]: v === "__all__" ? "" : v }));
  const clear = () => setFilters({ organization_id: "", action: "", entity: "", user_email: "", q: "" });

  const hasActive = useMemo(() => Object.values(filters).some(v => v && v.trim && v.trim().length > 0), [filters]);

  return (
    <div data-testid="platform-audit-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Supporthistorie & auditlog</h1>
          <p className="text-sm text-neutral-500 mt-1">Volledig platform-wide auditspoor. Filter per organisatie, actie of gebruiker.</p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          data-testid="audit-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Verversen
        </Button>
      </div>

      {/* Filterbalk */}
      <Card className="p-4 bg-white border-neutral-200 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Organisatie</label>
            <Select value={filters.organization_id || "__all__"} onValueChange={set("organization_id")}>
              <SelectTrigger data-testid="filter-organization"><SelectValue placeholder="Alle organisaties" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Alle organisaties</SelectItem>
                {orgs.map(o => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Actie</label>
            <Select value={filters.action || "__all__"} onValueChange={set("action")}>
              <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Alle acties" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__all__">Alle acties</SelectItem>
                {facets.actions.map(a => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Entiteit</label>
            <Select value={filters.entity || "__all__"} onValueChange={set("entity")}>
              <SelectTrigger data-testid="filter-entity"><SelectValue placeholder="Alle entiteiten" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="__all__">Alle entiteiten</SelectItem>
                {facets.entities.map(e => (<SelectItem key={e} value={e}>{e}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Gebruiker (email)</label>
            <Input
              data-testid="filter-user"
              value={filters.user_email}
              onChange={e => set("user_email")(e.target.value)}
              placeholder="bijv. directeur@..."
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1 block">Zoeken (vrije tekst)</label>
            <Input
              data-testid="filter-query"
              value={filters.q}
              onChange={e => set("q")(e.target.value)}
              placeholder="reden / id / naam"
            />
          </div>
        </div>
        {hasActive && (
          <div className="mt-3">
            <Button size="sm" variant="ghost" onClick={clear} className="text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100" data-testid="filter-clear">
              <X className="h-3.5 w-3.5 mr-1" /> Filters wissen
            </Button>
          </div>
        )}
      </Card>

      {/* Resultaten */}
      <Card className="p-0 bg-white border-neutral-200 overflow-hidden">
        <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-xs text-neutral-500" data-testid="audit-count">
            {loading ? "Laden…" : `${items.length} record${items.length === 1 ? "" : "s"}`}
          </div>
        </div>
        {items.length === 0 && !loading ? (
          <div className="p-6 text-sm text-neutral-500 text-center" data-testid="audit-empty">
            <ScrollText className="h-6 w-6 mx-auto mb-2 opacity-40" />
            Geen audit-events gevonden voor deze filters.
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {items.map((it, i) => (
              <div key={it.id || i} className="p-3 hover:bg-emerald-50/40" data-testid={`audit-row-${i}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="text-[11px] font-mono text-neutral-500 shrink-0 w-40">
                    <Clock className="h-3 w-3 inline mr-1 -mt-0.5" />
                    {(it.created_at || "").slice(0, 19).replace("T", " ")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">{it.action || "—"}</Badge>
                      <Badge className="bg-neutral-100 text-neutral-700 border border-neutral-200 text-[10px]">{it.entity || "—"}</Badge>
                      {it.entity_id && <span className="text-[11px] font-mono text-neutral-500">{it.entity_id}</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-neutral-700 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" /> {it.user_email || it.user_name || "system"}
                        {it.user_role && <span className="text-neutral-500">({it.user_role})</span>}
                      </span>
                      <span className="flex items-center gap-1 text-neutral-500">
                        <Building2 className="h-3 w-3" /> {it.organization_name || "—"}
                      </span>
                    </div>
                    {it.reason && <div className="mt-1 text-xs text-neutral-500 italic">"{it.reason}"</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
